import React, { useState, useRef } from 'react';
import { PlusIcon, GlobeAltIcon, Bars3Icon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DomainMapping } from '../../types';

interface DomainMappingsListProps {
  mappings: DomainMapping[];
  onAddMapping: () => void;
  onEditMapping: (id: string) => void;
  onDeleteMapping: (id: string) => void;
  onToggleStatus: (id: string, active: boolean) => void;
  onReorderMappings: (sourceId: string, targetId: string) => void;
}

const DomainMappingsList: React.FC<DomainMappingsListProps> = ({
  mappings,
  onAddMapping,
  onEditMapping,
  onDeleteMapping,
  onToggleStatus,
  onReorderMappings
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteDomainName, setDeleteDomainName] = useState<string>('');
  const dragNode = useRef<HTMLTableRowElement | null>(null);
  
  const filteredMappings = mappings.filter(mapping => 
    mapping.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mapping.backendName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, id: string) => {
    if (!id) {
      e.preventDefault();
      alert('This domain mapping cannot be dragged because it has no ID.');
      return;
    }
    setDraggedId(id);
    dragNode.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    
    // Add a class to the dragged item to style it
    setTimeout(() => {
      if (dragNode.current) {
        dragNode.current.classList.add('opacity-50');
      }
    }, 0);

    // Required for Firefox
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    if (dragNode.current) {
      dragNode.current.classList.remove('opacity-50');
      dragNode.current = null;
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    if (e.currentTarget !== dragNode.current) {
      e.currentTarget.classList.add('bg-zinc-700/30');
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-zinc-700/30');
  };

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, targetId: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-zinc-700/30');
    
    if (draggedId && draggedId !== targetId) {
      onReorderMappings(draggedId, targetId);
    }
  };

  const handleDeleteMapping = (id: string, domain: string) => {
    setConfirmDelete(id);
    setDeleteDomainName(domain);
  };

  const confirmDeleteMapping = (id: string) => {
    onDeleteMapping(id);
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-zinc-100 flex items-center">
          <GlobeAltIcon className="h-6 w-6 mr-2 text-indigo-500" />
          Domain Mappings
        </h2>
        <button
          type="button"
          onClick={onAddMapping}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New Domain Mapping
        </button>
      </div>
      
      {/* Description */}
      <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-300">
        <p>Domain mappings define which domains are routed to which backends in your HAProxy configuration. Each mapping can have an associated pattern and priority.</p>
        <p className="mt-2">Drag and drop mappings to change their order. Higher mappings take precedence over lower ones.</p>
      </div>

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search domains or backends..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
        />
      </div>

      {/* Mappings list */}
      {mappings.length === 0 ? (
        <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-8 text-center">
          <GlobeAltIcon className="mx-auto h-12 w-12 text-zinc-500" />
          <h3 className="mt-2 text-sm font-medium text-zinc-400">No domain mappings found</h3>
          <p className="mt-1 text-sm text-zinc-500">Get started by creating a new domain mapping.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={onAddMapping}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New Domain Mapping
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/30">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-800/50">
              <tr>
                <th scope="col" className="w-10 px-2 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider"></th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Domain</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Backend</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 bg-transparent">
              {filteredMappings.map((mapping) => (
                <tr 
                  key={mapping.id} 
                  className="hover:bg-zinc-800/30 transition-colors duration-150"
                  draggable
                  onDragStart={(e) => handleDragStart(e, mapping.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, mapping.id)}
                >
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center">
                      <Bars3Icon className="h-5 w-5 text-zinc-500 cursor-move" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-200">
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 mr-3 text-zinc-500" />
                      {mapping.domain}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                    {String(mapping.backendName as string) || 'Unknown Backend'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={mapping.active}
                          onChange={() => {
                            if (!mapping.id) {
                              alert('Cannot change status because this domain mapping has no ID.');
                              return;
                            }
                            onToggleStatus(mapping.id, !mapping.active);
                          }}
                        />
                        <div className="relative w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        <span className="ml-2 text-sm text-zinc-300">{mapping.active ? 'Active' : 'Inactive'}</span>
                      </label>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (!mapping.id) {
                            alert('This domain mapping cannot be edited because it has no ID.');
                            return;
                          }
                          onEditMapping(mapping.id);
                        }}
                        className="text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
                        title="Edit Mapping"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!mapping.id) {
                            alert('This domain mapping cannot be deleted because it has no ID.');
                            return;
                          }
                          handleDeleteMapping(mapping.id, mapping.domain);
                        }}
                        className="text-red-400 hover:text-red-300 transition-colors duration-150"
                        title="Delete Mapping"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-zinc-800/50 rounded-lg p-4 text-xs text-zinc-400">
        <p>Domain mappings are stored in the HAProxy hosts.map file and used for routing traffic to the appropriate backend. Drag and drop to change the order of priority.</p>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10">
            <div className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-3">Confirm Delete</h3>
              <p className="text-sm text-zinc-300 mb-6">
                Are you sure you want to delete the domain mapping for "{deleteDomainName}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteMapping(confirmDelete)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainMappingsList; 